import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Modal, Button, Input, Form, Notification, Spin } from "@arco-design/web-react";

import { IconAttachment, IconEdit, IconImage } from "@arco-design/web-react/icon";
import { PostApi } from "../types/types";
import {
  MAX_IMAGES_COUNT,
  MAX_POST_LENGTH,
  MAX_TITLE_LENGTH,
  SUPPORTED_DOCUMENT_TYPES,
  SUPPORTED_IMAGE_TYPES,
} from "../constants";
import { EmojiClickData } from "emoji-picker-react";
import IconFace from "../../../../../assets/icons/IconFace";
import { getButtonTextColor, getButtonColor } from "../../../../../utils/cssVariables";
import { PostAttachmentsPreview } from "./PostAttachments/PostAttachmentsPreview";
import { PostFormValues } from "./CreatePostModal";
import { validatePostAttachments } from "../../../../../utils/validators/validatePostAttachments";
import { createUpdatedPostData } from "../../../../../utils/createUpdatedPostData";
import { EmojiPickerModal } from "../../../../../components/EmojiPickerModal/EmojiPickerModal";

interface IUpdatePostModalProps {
  post: PostApi;
  isPostUpdating: boolean;
  onUpdatePost: (postId: number, updatedPost: FormData) => void;
}

const UpdatePostModal = ({ post, isPostUpdating, onUpdatePost }: IUpdatePostModalProps) => {
  const { t } = useTranslation();

  const [form] = Form.useForm<PostFormValues>();
  const [visible, setVisible] = useState(false);

  const [showEmojiPicker, setShowEmojiPicker] = useState(false);

  const contentInputRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);

  const [attachments, setAttachments] = useState({
    documentFile: undefined as File | undefined,
    currentImageFiles: [] as File[],
    currentImages: [] as string[],
    currentDocument: '',
  });

  const showModal = () => {
    setVisible(true);
  };

    const handleEmojiClick = (emojiData: EmojiClickData) => {
      const content = form.getFieldValue('content');
      form.setFieldValue('content', content + emojiData.emoji);
      setShowEmojiPicker(false);
  
      contentInputRef.current?.focus();
      
    };
  
    const toggleEmojiPicker = () => {
      setShowEmojiPicker(!showEmojiPicker);
    };

  const handleDocumentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files?.[0]) return;

    const error = validatePostAttachments(e.target.files[0], false, t);
    if (error) return Notification.error({ content: error });

    setAttachments({
      currentImageFiles: attachments.currentImageFiles,
      currentImages: attachments.currentImages,
      documentFile: e.target.files[0],
      currentDocument: '', 
    });
    e.target.value = '';
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;

    const newImages = Array.from(e.target.files);
    const validImages: File[] = [];

    for (const file of newImages) {
      const error = validatePostAttachments(file, true, t);
      if (error) {
        Notification.error({ content: `${file.name}: ${error}` });
        continue;
      }
      validImages.push(file);
    }


    const totalImages = attachments.currentImageFiles.length + validImages.length;
    if (totalImages > MAX_IMAGES_COUNT) {
      return Notification.warning({
        content: t('form_validation_messages.max_images_exceeded', { count: MAX_IMAGES_COUNT })
      });
    }

    setAttachments(prev => ({
      ...prev,
      currentImageFiles: [...prev.currentImageFiles, ...validImages].slice(0, MAX_IMAGES_COUNT)
    }));
    e.target.value = '';
  };

  const removeAttachment = (type: 'image' | 'imageFile' | 'document' | 'documentFile', value: string) => {
    setAttachments(prev => {
      const newState = { ...prev };
      switch (type) {
        case 'image':
          newState.currentImages = prev.currentImages.filter(img => img !== value);
          break;
        case 'imageFile':
          newState.currentImageFiles = prev.currentImageFiles.filter(file => file.name !== value);
          break;
        case 'document':
          newState.currentDocument = '';
          break;
        case 'documentFile':
          newState.documentFile = undefined;
          break;
      }
      return newState;
    });
  };
  
  const handleUpdate = (formValues: PostFormValues) => {

    const formData = createUpdatedPostData({
      post: {
        ...post,
        images: attachments.currentImages.join(';;;'),
        document_url: attachments.currentDocument
      },
      ...formValues,
      documentFile: attachments.documentFile,
      imageFiles: attachments.currentImageFiles
    });

    onUpdatePost(post.id, formData);
    resetForm();
  };

  const resetForm = () => {
    form.resetFields();
    setAttachments({
      documentFile: undefined,
      currentImageFiles: [],
      currentImages: post.images ? post.images.split(';;;').filter(Boolean) : [],
      currentDocument: post.document_url,
    });
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (imageInputRef.current) imageInputRef.current.value = '';
  };

  const handleClose = () => {
    if(!isPostUpdating){
      resetForm();
      setVisible(false);
    }
  }

  useEffect(() => {
    if (visible) {
      form.setFieldsValue({
        heading: post.heading,
        content: post.content
      });
      setAttachments({
        documentFile: undefined,
        currentImageFiles: [],
        currentImages: post.images ? post.images.split(';;;').filter(Boolean) : [],
        currentDocument: post.document_url,
      });
    }
  }, [visible, post, form]);

  useEffect(() => {
    setVisible(isPostUpdating);
  }, [isPostUpdating])

  return (
    <div>
      <Button
        style={{
          padding: 0,
          backgroundColor: "transparent",
        }}
        onClick={showModal}
      >
        <IconEdit />
      </Button>

      <Modal
        className="feed-modal"
        title={t('home_page.update_post')}
        visible={visible}
        footer={null}
        onCancel={handleClose}
      >
        {isPostUpdating ? <Spin className="feed-modal__loader" size={24} dot /> : <Form 
          form={form}
          layout="vertical"
          onSubmit={handleUpdate}
          autoComplete="off"
        >
          <Form.Item
            label={t('home_page.post_title_label')}
            field="heading"
            rules={[
              { required: true, message: t('notification_messages.post_title_empty') },
              { max: MAX_TITLE_LENGTH, message: t('notification_messages.post_title_length_exceeded') }
            ]}
          >
            <Input
              className="post-title__input"
              placeholder={t('home_page.post_title_placeholder')}
              maxLength={MAX_TITLE_LENGTH}
              showWordLimit
            />
          </Form.Item>
          <Form.Item 
            label={t('home_page.post_content_label')}
            field="content"
            rules={[
              { required: true, message: t('notification_messages.post_content_empty') },
              { max: MAX_POST_LENGTH, message: t('notification_messages.post_content_length_exceeded') }
            ]}
          >
            <Input.TextArea
              className='post-content__input'
              placeholder={t('home_page.post_content_placeholder')}
              rows={5}
              showWordLimit
              maxLength={MAX_POST_LENGTH}
            />
          </Form.Item>
          <PostAttachmentsPreview 
            documentFile={attachments.documentFile}
            document_url={attachments.currentDocument}
            file_name={ post.file_name}
            imageFiles={attachments.currentImageFiles}
            images={attachments.currentImages}
            onRemoveDocumentFile={() => removeAttachment('documentFile', '')}
            onRemoveDocument={() => removeAttachment('document', '')}
            onRemoveImage={(url) => {removeAttachment('image', url)}}
            onRemoveImageFile={(name) => removeAttachment('imageFile', name)}
            onAddImageFile={() => imageInputRef.current?.click()}
          />
          <div className="post-actions__wrapper">
            <div className="post-actions__content">
              <Button
                className="post-action-button"
                onClick={toggleEmojiPicker}
              >
                <IconFace className="post-action-button-icon" />
              </Button>
              <Form.Item>
                {
                  <Button
                    className="post-action-button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={!!attachments.documentFile}
                  >
                    <IconAttachment className="post-action-button-icon" />
                  </Button>
                }
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleDocumentChange}
                  accept={SUPPORTED_DOCUMENT_TYPES.join(',')}
                  style={{ display: 'none' }}
                />
              </Form.Item>
              <Form.Item>
                <Button
                  onClick={() => {
                    imageInputRef.current?.click();
                    imageInputRef.current!.value = '';
                  }}
                  icon={<IconImage />}
                  disabled={attachments.currentImageFiles.length + (attachments.currentImages?.length || 0) >= MAX_IMAGES_COUNT}
                >
                  {!!attachments.currentImageFiles.length && `(${attachments.currentImageFiles.length + (attachments.currentImages?.length || 0) }/${MAX_IMAGES_COUNT})`}
                </Button>
                <input
                  type="file"
                  ref={imageInputRef}
                  onChange={handleImageChange}
                  accept={SUPPORTED_IMAGE_TYPES.join(',')}
                  multiple
                  style={{ display: 'none' }}
                />
              </Form.Item>
            </div>
            <Button
              htmlType="submit"
              style={{
                color: getButtonTextColor(),
                backgroundColor: getButtonColor(),
              }}
              shape="round"
            >
              {t('common.update_button')}
            </Button>
            
            <EmojiPickerModal
              visible={showEmojiPicker}
              onClose={() => setShowEmojiPicker(false)}
              onEmojiClick={handleEmojiClick}
            />
          </div>
        </Form>}
      </Modal>
    </div>
  );
};

export default UpdatePostModal;